import React, { useEffect, useRef, useCallback } from "react";
import { useState } from "react";
import {
  useEditorState,
} from "@/store";
import { debounce } from "lodash";
// Must be the first Blockly-related import: patches Blockly.fieldRegistry
// before any field plugin below can self-register on import.
import "./blocklyFieldRegistrySafety";
import * as Blockly from "blockly";
import type { BlocklyOptions } from "blockly";
import { pythonGenerator } from "blockly/python";
import { ContentHighlight } from "@blockly/workspace-content-highlight";
import { registerContinuousToolbox } from "@blockly/continuous-toolbox/dist";
import { FixedEdgesMetricsManager } from "@blockly/fixed-edges";
import * as SuggestedBlocks from "@blockly/suggested-blocks";
import * as Tr from "blockly/msg/tr";
import * as En from "blockly/msg/en";
import { FieldAngle, registerFieldAngle } from "@blockly/field-angle";
import { FieldColour, registerFieldColour } from "@blockly/field-colour";
import { FieldSlider } from "@blockly/field-slider";
import { FieldGridDropdown } from "@blockly/field-grid-dropdown";
import { FieldBitmap } from "@blockly/field-bitmap";
import { FieldMultilineInput, registerFieldMultilineInput } from "@blockly/field-multilineinput";
import { FieldDate } from "@blockly/field-date";
import { useTranslation } from "react-i18next";
import "@blockly/toolbox-search";

import {
  Logger,
  postMessageToRef,
  BlocklyInvalidXml,
  BlocklyInvalidBlock,
  BlocklyCodeGenerationError,
  BlocklyError
} from "@/utils";

import { RidersDark, RidersLight } from "./BlocklyThemes";
import workspaceConfiguration from "./workspaceConfiguration.json";
import arduinoGenerator from "./arduinoGenerator";
import { overrideBlocklyVariables } from "./blocklyVariablesOverride";
import { onAnyVariableChange } from "./variableListener";
import { FS, type WriteOptions } from "@/singletons/FileSystem";
import * as Sentry from "@sentry/react";

// FieldAngle/FieldSlider (FieldNumber-based) ghost their SVG text behind the in-place input and leave
// it unfocused under our zoomed workspace; hide the text while editing and re-focus the input.
function patchDropdownNumberField(proto: any) {
  const show = proto.showEditor_, dispose = proto.widgetDispose_;
  proto.showEditor_ = function (this: any, e?: Event) {
    show.call(this, e);
    const t = this.getTextElement?.(); if (t) t.style.display = "none";
    setTimeout(() => { const i = this.htmlInput_; if (i) { i.focus(); i.select(); } }, 0);
  };
  proto.widgetDispose_ = function (this: any) {
    dispose.call(this);
    const t = this.getTextElement?.(); if (t) t.style.display = "";
  };
}

try {
  patchDropdownNumberField(FieldAngle.prototype);
  patchDropdownNumberField(FieldSlider.prototype);
} catch (error) {
  Sentry.captureException(new BlocklyError("Failed to patch FieldAngle or FieldSlider", error as Error));
}

export interface BlocklyProps {
  className?: string;
  children?: React.ReactNode;
  file_path: string;
  xml_path: string;
  initialXML?: string | undefined;
  plugins?: string[];
  renderer?: "zelos" | "geras" | "thrasos";
  config?: BlocklyOptions;
  readOnly?: boolean;
  showMessage?: (type: string, message: string) => void;
}

const setDarkThemeTextColor = (theme: string) => {
  const labels = document.querySelectorAll(".blocklyTreeLabel");
  for (let i = 0; i < labels.length; i++) {
    if (theme === "dark") {
      labels[i].classList.add("blocklyTreeLabelDark");
    } else {
      labels[i].classList.remove("blocklyTreeLabelDark");
    }
  }
};

const debouncedWriteFileFactory = (setter: (path: string, code: string, writeOptions?: WriteOptions) => void) => {
  return debounce((path: string, code: string, writeOptions?: WriteOptions) => {
    setter(path, code, writeOptions);
  }, 200);
};



let isContinuousToolboxRegistered = false;
export const BlocklyEditor: React.FC<BlocklyProps> = ({
  className,
  file_path,
  xml_path,
  initialXML,
  plugins = [],
  renderer = "zelos",
  config = {},
  readOnly = false,
  showMessage
}) => {
  const { t } = useTranslation();

  if (plugins.includes("field-angle")) {
    registerFieldAngle();
    (window as any).FieldAngle = FieldAngle;
  }

  if (plugins.includes("field-colour")) {
    registerFieldColour();
    (window as any).FieldColour = FieldColour;
  }

  if (plugins.includes("field-slider")) {
    Blockly.fieldRegistry.register('field_slider', FieldSlider);
    (window as any).FieldSlider = FieldSlider;
  }

  if (plugins.includes("field-grid-dropdown")) {
    Blockly.fieldRegistry.register('field_grid_dropdown', FieldGridDropdown);
    (window as any).FieldGridDropdown = FieldGridDropdown;
  }

  if (plugins.includes("field-bitmap")) {
    Blockly.fieldRegistry.register("field_bitmap", FieldBitmap);
    (window as any).FieldBitmap = FieldBitmap;
  }

  if (plugins.includes("field-multilineinput")) {
    registerFieldMultilineInput();
    (window as any).FieldMultilineInput = FieldMultilineInput;
  }

  if (plugins.includes("field-date")) {
    Blockly.fieldRegistry.register("field_date", FieldDate);
    (window as any).FieldDate = FieldDate;
  }

  if (!isContinuousToolboxRegistered) {
    registerContinuousToolbox();
    isContinuousToolboxRegistered = true;
  }

  const isZelos = renderer === "zelos";
  const blocklyDiv = useRef(null);
  const shadowBlocklyDiv = useRef(null);

  const { language, theme, godotContainerRef, platform } = useEditorState();

  const [workspaceXml, setWorkspaceXml] = useState<string | undefined>(
    undefined
  );
  const [toolboxCategories, setToolboxCategories] = useState<any>();
  const [scriptLoaded, setScriptLoaded] = useState(false);

  const [blocklyTheme, setBlocklyTheme] = useState(
    theme === "dark" ? "riders-dark" : "riders-light"
  );

  const codeFileDebouncer = debouncedWriteFileFactory(FS.write);
  const xmlFileDebouncer = debouncedWriteFileFactory(FS.write);



  const fetchBlockData = useCallback(async () => {
    try {
      window.Blockly = Blockly;

      switch (file_path.split(".").pop()) {
        case "py":
          window.BlocklyPython = pythonGenerator;
          break;
        case "ino":
          window.BlocklyArduino = arduinoGenerator;
          break;
      }

      if (language === "tr") {
        window.Blockly.setLocale(Tr);
      }

      window.Blockly.Theme.defineTheme("riders-light", RidersLight(true));
      window.Blockly.Theme.defineTheme("riders-dark", RidersDark(true));

      const scripts = [
        document.createElement("script"),
        document.createElement("script"),
      ];

      try {
        const xmlFileContent = await FS.read(xml_path);
        if (xmlFileContent) {
          setWorkspaceXml(xmlFileContent);
        } else {
          setWorkspaceXml(
            initialXML || '<xml xmlns="http://www.w3.org/1999/xhtml"></xml>'
          );
        }
      } catch (_error) {
        setWorkspaceXml(
          initialXML || '<xml xmlns="http://www.w3.org/1999/xhtml"></xml>'
        );
      }

      const data = useEditorState.getState().toolboxFile;
      // TODO(Baturay:: better fix here later.
      let toolbox;
      try {
        toolbox = JSON.parse(data || "{}");
        // Blockly expects a toolbox object to have a 'contents' array.
        if (typeof toolbox === "object" && toolbox !== null && !Array.isArray(toolbox) && !toolbox.contents) {
          toolbox = { kind: "categoryToolbox", contents: [] };
        }
      } catch (e) {
        // If it fails to parse as JSON, it might be an XML string or invalid.
        toolbox = data || { kind: "categoryToolbox", contents: [] };
      }

      setToolboxCategories(toolbox);

      const initText = useEditorState.getState().initBlocksFile;
      const generateText = useEditorState.getState().generateBlocksFile;

      scripts[0].type = "text/javascript";
      scripts[0].text = initText || "";
      scripts[1].type = "text/javascript";
      scripts[1].text = generateText || "";

      // Reset blockly_init_ready before injecting so we can detect success
      (window as any).blockly_init_ready = false;

      // Always log first 600 chars so we can diagnose 'super' SyntaxErrors even if catch isn't entered
      if ((initText || "").length > 0) {
        console.log("[Blockly] initblocks.js first 600 chars:", (initText || "").substring(0, 600));
      }

      try {
        document.body.appendChild(scripts[0]);
      } catch (scriptError) {
        console.error("[Blockly] initblocks.js injection failed:", scriptError);
        console.log("[Blockly] initblocks.js full content:", initText);
        Sentry.captureException(new BlocklyError("initblocks.js injection failed", scriptError as Error));
      }

      try {
        document.body.appendChild(scripts[1]);
      } catch (scriptError) {
        console.error("[Blockly] generateblocks.js injection failed:", scriptError);
        Sentry.captureException(new BlocklyError("generateblocks.js injection failed", scriptError as Error));
      }

      // If blocks didn't register, use empty XML to prevent BlocklyInvalidBlock on workspace init.
      // The workspace XML will be restored on the next remount after Godot sends initblocks.js.
      if (!(window as any).blockly_init_ready) {
        if ((initText || "").length > 0) {
          console.warn("[Blockly] Blocks did not register despite non-empty initblocks.js — check for syntax errors above");
        }
        setWorkspaceXml('<xml xmlns="http://www.w3.org/1999/xhtml"></xml>');
      }

      setScriptLoaded(true);

      if (language === "tr") {
        (window as any).SetLanguage?.("tr");
      }

    } catch (error) {
      Sentry.captureException(new BlocklyError("Error in fetchBlockData function", error as Error));
    }
  }, [xml_path]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const readyDebouncer = useCallback(
    debounce(() => {
      postMessageToRef(godotContainerRef, {
        action: "setEditorLayoutResponse",
        status: "blocklyIsReady",
      });
      useEditorState.getState().setBlocklyState("INITED");
      Blockly.Events.disable();
      const workspace = (window as any).mainWorkspace;
      const userVars: {
        id: string;
        name: string;
        type: string;
      }[] | undefined = useEditorState
        .getState()
        .getBlocklyVariables(useEditorState.getState().blocklyCodePath, "variables");

      try {
        userVars?.forEach((v) => {
          const existingVar = workspace?.getVariable(v.name, v.type);
          if (!existingVar) {
            workspace?.createVariable(v.name, v.type, v.id);
          }
        });

      } catch (error) {
        Sentry.captureException(new BlocklyError("Error in readyDebouncer function", error as Error));
      }

      Blockly.Events.enable();
      overrideBlocklyVariables(t, language);
      onAnyVariableChange(workspace!, (e) => {
        postMessageToRef(godotContainerRef, {
          action: "onVariableListChange",
          type: e.type,
          data: e.vars.map((v) => ({
            id: v.id,
            name: v.name,
          })),
        });
      });
      (window as any)?.updateFlyOut?.();
    }, 500),
    [godotContainerRef]
  );

  useEffect(() => {
    fetchBlockData().then(() => {
      readyDebouncer();
    });
    return () => {
      useEditorState.getState().setBlocklyState("NOTINITED");
    }
  }, []);

  useEffect(() => {
    setDarkThemeTextColor(theme);
    setBlocklyTheme(theme === "dark" ? "riders-dark" : "riders-light");
  }, [theme]);

  useEffect(() => {
    if (scriptLoaded && window.Blockly) {
      if (language === "tr") {
        window.Blockly.setLocale(Tr);
      } else {
        window.Blockly.setLocale(En);
      }
      overrideBlocklyVariables(t, language);
    }
  }, [language, scriptLoaded, t]);

  useEffect(() => {
    try {
      const dynamicPlugins: BlocklyOptions["plugins"] = {};

      dynamicPlugins.metricsManager = FixedEdgesMetricsManager;

      if (plugins.includes("continuous-toolbox")) {
        dynamicPlugins.flyoutsVerticalToolbox = "ContinuousFlyout";
        dynamicPlugins.metricsManager = "ContinuousMetrics";
        dynamicPlugins.toolbox = "ContinuousToolbox";
      }

      if (blocklyDiv.current && scriptLoaded) {
        const shadowWorkspace = Blockly.inject(shadowBlocklyDiv.current!, {
          readOnly,
          toolbox: toolboxCategories,
          media: workspaceConfiguration.media,
          renderer,
          zoom: {
            ...workspaceConfiguration.zoom,
            startScale: isZelos ? 0.75 : 0.9,
          },
          theme: blocklyTheme,
          grid:
            theme === "dark"
              ? { spacing: 20, length: 3, colour: "#000", snap: true }
              : isZelos
                ? { spacing: 20, length: 3, colour: "#ccc", snap: true }
                : undefined,
          move: {
            scrollbars: {
              horizontal: true,
              vertical: true,
            },
            drag: true,
            wheel: true,
          },
          plugins: dynamicPlugins,
          ...config,
        });
        (window as any).shadowWorkspace = shadowWorkspace;

        const workspace = Blockly.inject(blocklyDiv.current, {
          readOnly,
          toolbox: toolboxCategories,
          media: workspaceConfiguration.media,
          renderer,
          zoom: {
            ...workspaceConfiguration.zoom,
            startScale: isZelos ? 0.75 : 0.9,
          },
          theme: blocklyTheme,
          grid:
            theme === "dark"
              ? { spacing: 20, length: 3, colour: "#000", snap: true }
              : isZelos
                ? { spacing: 20, length: 3, colour: "#ccc", snap: true }
                : undefined,
          move: {
            scrollbars: {
              horizontal: true,
              vertical: true,
            },
            drag: true,
            wheel: true,
          },
          plugins: dynamicPlugins,
          ...config,
        });
        (window as any).mainWorkspace = workspace;



        let xmlDom;
        try {
          xmlDom = Blockly.utils.xml.textToDom(workspaceXml!);
        } catch (e) {
          throw new BlocklyInvalidXml("blockly.invalidXml", e as Error);
        }
        try {
          Blockly.Xml.appendDomToWorkspace(xmlDom, workspace);
        } catch (e) {
          throw new BlocklyInvalidBlock("blockly.invalidBlock", e as Error);
        }


        // NOTE: multi-block selection is currently disabled. Blockly 12's
        // breaking focus-manager change (google/blockly#9004) requires every
        // selectable object to implement IFocusableNode, which
        // @varwin/blockly-plugin-workspace-multiselect (and the upstream
        // mit-cml/workspace-multiselect it forks) does not yet do — it throws
        // "Cannot read properties of null (reading 'canBeFocused')" as soon as
        // a selection is made or cleared. Re-enable once a Blockly-12-compatible
        // release exists (tracked upstream at mit-cml/workspace-multiselect#133,
        // still an unmerged draft as of writing).

        // ResizeObserver to handle resizing of the Blockly workspace:
        const observer = new ResizeObserver(() => {
          Blockly.svgResize(workspace);
          Blockly.svgResize(shadowWorkspace);
        });
        observer.observe(blocklyDiv.current);

        if (plugins.includes("suggested-blocks")) {
          SuggestedBlocks.init(workspace);
        }

        if (plugins.includes("content-highlight")) {
          const cH = new ContentHighlight(workspace);
          cH.init();
        }

        workspace.addChangeListener(async (e) => {
          try {
            if (plugins.includes("disable-orphans") || platform === "mebkit") {
              Blockly.Events.disableOrphans(e);
            }

            if (e.type === Blockly.Events.FINISHED_LOADING) {
              return;
            }

            let xml;
            try {
              xml = Blockly.Xml.domToText(
                Blockly.Xml.workspaceToDom(workspace)
              );

            } catch (e) {
              throw new BlocklyInvalidBlock("blockly.invalidBlock", e as Error);
            }

            setWorkspaceXml(xml);
            xmlFileDebouncer(xml_path, xml);

            let code = "";
            try {
              switch (file_path.split(".").pop()) {
                case "py":
                  code = pythonGenerator.workspaceToCode(workspace);
                  break;
                case "ino":
                  code = arduinoGenerator.workspaceToCode(workspace);
                  break;
              }

            } catch (e) {
              throw new BlocklyCodeGenerationError("blockly.codeGenerationError", e as Error);
            }

            codeFileDebouncer(file_path, code);

          } catch (e) {
            if (e instanceof BlocklyInvalidBlock) {
              showMessage?.("error", "blockly.invalidBlock");
            } else if (e instanceof BlocklyCodeGenerationError) {
              showMessage?.("error", "blockly.codeGenerationError");
            } else {
              showMessage?.("error", "blockly.generic");
            }
            Sentry.captureException(new BlocklyError("Error in blocklyWrapper function", e as Error));
            throw e;
          };
        });

        return () => {
          observer.disconnect();
          workspace.dispose();
          shadowWorkspace.dispose();
        };
      }

    } catch (error) {
      if (error instanceof BlocklyInvalidXml) {
        showMessage?.("error", "blockly.invalidXml")
      } else if (error instanceof BlocklyInvalidBlock) {
        showMessage?.("error", "blockly.invalidBlock");
      }
      Sentry.captureException(new BlocklyError("Error in blocklyWrapper function", error as Error));
      throw error;
    }
  }, [scriptLoaded]);

  if (!scriptLoaded) {
    return (
      <div className="h-full w-full" id="blockly_wrapper">
        <div className="h-full flex flex-col justify-center items-center">
          <span className="mb-6 text-2xl">Loading Block Coding</span>
          <div style={{ display: "flex" }}>
            <div className="dot dot--one"></div>
            <div className="dot dot--two"></div>
            <div className="dot dot--three"></div>
            <div className="dot dot--four"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        ref={blocklyDiv}
        style={{ height: "100%", width: "100%" }}
        className={className}
      ></div>
      <div
        ref={shadowBlocklyDiv}
        style={{ height: "100%", width: "100%", display: "none" }}
        className={className}
      ></div>
    </>
  );
};
